import { JSX } from "react";
import { motion } from "motion/react";
import { 
    CalendarIcon, 
    BellIcon, 
    ClipboardIcon, 
    UsersIcon, 
    ChatBubbleLeftRightIcon,
    ShieldCheckIcon,
    HeartIcon,
    GlobeAltIcon,
    UserGroupIcon,
    ChartBarIcon
} from "@heroicons/react/24/outline";

const styles = {
    section: "mx-auto max-w-7xl px-6 py-24 my-8",
    header: "text-center mb-16",
    title: "text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl",
    subtitle: "mt-6 text-lg leading-8 text-gray-600 max-w-3xl mx-auto",
    grid: "grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-3",
    categoryWrap: "p-8 bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300",
    categoryTitle: "text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3",
    feature: "flex items-start space-x-4 mb-8 last:mb-0",
    iconWrap: "flex-shrink-0 p-2 bg-green-50 rounded-lg",
    icon: "h-6 w-6 text-green-600",
    featureTitle: "text-lg font-semibold text-gray-900",
    featureDesc: "mt-1 text-base text-gray-600 leading-relaxed",
};

const categories = [
    {
        title: "Gestión Inteligente",
        icon: <CalendarIcon className="h-6 w-6 text-green-600" />,
        features: [
            { title: "Agenda Automatizada", desc: "Organiza citas sin solapamientos y optimiza tu tiempo real.", icon: <CalendarIcon className={styles.icon} /> },
            { title: "Recordatorios Automáticos", desc: "Reduce las inasistencias con alertas directas a los dueños.", icon: <BellIcon className={styles.icon} /> },
            { title: "Soporte Dedicado", desc: "Equipo experto para ayudarte a maximizar la plataforma.", icon: <ChatBubbleLeftRightIcon className={styles.icon} /> },
        ]
    },
    {
        title: "Control Clínico",
        icon: <ClipboardIcon className="h-6 w-6 text-green-600" />,
        features: [
            { title: "Historial Digital", desc: "Acceso instantáneo a datos médicos, vacunas y tratamientos.", icon: <ClipboardIcon className={styles.icon} /> },
            { title: "Seguimiento de Salud", desc: "Control preciso de la evolución de cada paciente.", icon: <ChartBarIcon className={styles.icon} /> },
            { title: "Gestión de Vacunas", desc: "Alertas automáticas para refuerzos y revisiones.", icon: <ShieldCheckIcon className={styles.icon} /> },
        ]
    },
    {
        title: "Crecimiento y Red",
        icon: <UsersIcon className="h-6 w-6 text-green-600" />,
        features: [
            { title: "Fidelización", desc: "Promociones personalizadas para mantener a tus clientes leales.", icon: <HeartIcon className={styles.icon} /> },
            { title: "Visibilidad Digital", desc: "Aparece en nuestra red de profesionales recomendados.", icon: <GlobeAltIcon className={styles.icon} /> },
            { title: "Red de Colaboración", desc: "Conéctate con otros expertos para una atención integral.", icon: <UserGroupIcon className={styles.icon} /> },
        ]
    },
];

const FeaturesSection = (): JSX.Element => (
    <section className={styles.section}>
        <div className={styles.header}>
            <motion.h2 
                className={styles.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
            >
                Todo lo que necesitas para una clínica de excelencia
            </motion.h2>
            <motion.p 
                className={styles.subtitle}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                viewport={{ once: true }}
            >
                 Hemos simplificado la complejidad administrativa para que puedas enfocarte en lo que realmente importa: el bienestar de tus pacientes.
            </motion.p>
        </div>

        <div className={styles.grid}>
            {categories.map((cat, idx) => (
                <motion.div 
                    key={cat.title} 
                    className={styles.categoryWrap}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    viewport={{ once: true }}
                >
                    <h3 className={styles.categoryTitle}>
                        {cat.icon}
                        {cat.title}
                    </h3>
                    <div>
                        {cat.features.map((feat) => (
                            <div key={feat.title} className={styles.feature}>
                                <div className={styles.iconWrap}>{feat.icon}</div>
                                <div>
                                    <h4 className={styles.featureTitle}>{feat.title}</h4>
                                    <p className={styles.featureDesc}>{feat.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            ))}
        </div>
    </section>
);

export default FeaturesSection;
